/**
 * Which URLs the ad pixels (Meta, TikTok, Google Ads) may see.
 *
 * The pixels read `location.href` and `document.referrer` themselves, so a URL is either safe to
 * show them as it is or the pixels must not run while it is current. Query parameters are an
 * allowlist: links we send carry secrets in the query (reset codes, OAuth codes, the `id` that
 * grants notification-preference changes, emails), and so does in-app state such as the
 * eligibility search's household size and income. Paths are a denylist, below.
 */
const ALLOWED_QUERY_PARAMS: ReadonlySet<string> = new Set([
  // Ad click ids, read by the pixels and by Amplitude attribution. A landing page that fails
  // this check loads no pixels, so its click id is lost: list every parameter the ad platforms
  // add to a click. Google Ads auto-tagging adds `gad_source` and `gad_campaignid` next to
  // `gclid`, and gtag's cross-domain linker adds `_gl`.
  'fbclid', 'gclid', 'gbraid', 'wbraid', 'gclsrc', 'dclid', 'gad_source', 'gad_campaignid', '_gl',
  'ttclid', 'msclkid',
  // Payment success pages: the billing interval, the trial-started flag and Stripe's Checkout
  // session id. A Purchase fires on /payment/deferred-success?interval=…&session_id=cs_…
  'interval', 'payment', 'session_id',
  // `true`/`false`: whether the drawer (the paywall among others) is open.
  'drawer',
]);

// create-stripe-session's `trial` (true/false) and `value` (the price) on the success URL. Only on
// /payment/…: elsewhere a `value` could be anything a later page puts there.
const PAYMENT_QUERY_PARAMS: ReadonlySet<string> = new Set(['trial', 'value']);

const isAllowedQueryParam = (key: string, pathname: string) =>
  key.startsWith('utm_') || ALLOWED_QUERY_PARAMS.has(key)
  || (PAYMENT_QUERY_PARAMS.has(key) && /^\/payment\//i.test(pathname));

/**
 * Paths the pixels must never see, whatever the query.
 */
const UNSAFE_PATHS: readonly RegExp[] = [
  // Opened from the reset email, whose link carries the email address and a live reset code.
  /^\/login\/reset-password(\/|$)/i,
  // OAuth redirect targets, which carry the authorization code and state.
  /^\/(callback|federated-login)(\/|$)/i,
  // Profile questionnaire steps. Which step a user is on follows from their earlier answers
  // (homeless-shelter, rental-assistance-types, accommodation-details, assets/too-large, …), so
  // the path is itself an answer. The whole numbered-step subtree is excluded so that a step
  // added later is covered too; the flows' start and `complete` pages stay visible.
  /^\/profile\/update\/(address-info|household-info|income-assets-info)\/\d+(\/|$)/i,
  // Shown only when every household member is a student.
  /^\/profile\/update\/household-info\/all-students(\/|$)/i,
  // Shown only when the household owns real estate in NYC.
  /^\/profile\/update\/income-assets-info\/real-estate-range-problem(\/|$)/i,
];

/** Whether the pixels may see `href`, an absolute URL, as it is. */
export function isPixelSafeUrl(href: string): boolean {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return false;
  }

  // Angular matrix params (`/path;key=value`) can carry state like a query does.
  if (url.pathname.includes(';') || UNSAFE_PATHS.some(pattern => pattern.test(url.pathname))) {
    return false;
  }

  // Anchors only: OAuth implicit flows put tokens in the fragment.
  if (url.hash !== '' && !/^#[\w-]*$/.test(url.hash)) {
    return false;
  }

  return [...url.searchParams.keys()].every(key => isAllowedQueryParam(key, url.pathname));
}

/**
 * Whether the pixels may see `referrer` (`document.referrer`). They report it on every event for
 * the life of the page, so a full page load that came from one of our own unsafe URLs keeps the
 * pixels off until the next full load. Other sites' URLs are theirs to keep out of referrers.
 */
export function isPixelSafeReferrer(referrer: string, origin: string): boolean {
  if (referrer === '') {
    return true;
  }

  try {
    if (new URL(referrer).origin !== origin) {
      return true;
    }
  } catch {
    return false;
  }

  return isPixelSafeUrl(referrer);
}
